import type { DatasetMapping } from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, FileSpreadsheet, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { DataText } from '../components/data/data-text';
import { CheckStep } from '../components/datasets/check-step';
import { ColumnsStep } from '../components/datasets/columns-step';
import { ImportRun } from '../components/datasets/import-run';
import { LayoutStep } from '../components/datasets/layout-step';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { useDataset, useStartImport, useSuggestMapping } from '../lib/datasets';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Dataset import wizard (task 25, PLAN §5 step 2): layout → columns → check and import. The proposal comes from the
// importer's rules (and the AI, where connected); the person confirms; the import itself is deterministic except for
// free-text cells, which the AI extraction reads into review.

export const Route = createFileRoute('/datasets/$id')({ component: DatasetPage });

const TOUR = {
  id: 'dataset-import',
  steps: [
    {
      target: '[data-tour="dataset-steps"]',
      title: 'Three steps',
      body: 'Check which rows are titles, legends, the header and sections, then what each column becomes, then look at a dry run before importing.',
      learnMore: 'import-a-dataset',
    },
    {
      target: '[data-tour="dataset-layout"]',
      title: 'Titles, legends and sections',
      body: 'Rows above the header are titles or legends; a legend like "Y = In favour" is used to read coded cells. Section rows give a category to the rows under them.',
    },
  ],
};

const STEPS = ['Rows', 'Columns', 'Check and import'] as const;

function DatasetPage() {
  useScreenTour(TOUR);
  const { id } = Route.useParams();
  const [layout, setLayout] = useState<{ sheet?: string; headerRow?: number }>({});
  const dataset = useDataset(id, layout);
  const { data: snap } = useSchema();
  const canWrite = useCan('content.write');
  const [step, setStep] = useState(0);
  const [mapping, setMapping] = useState<DatasetMapping | null>(null);
  const [editing, setEditing] = useState(false);
  const [startedId, setStartedId] = useState<string | null>(null);
  const suggest = useSuggestMapping(id);
  const start = useStartImport(id);
  const asked = useRef(false);

  const detail = dataset.data;
  const preview = suggest.data?.preview ?? detail?.preview;
  // A new proposal (another sheet or header row, or the AI's) replaces the mapping being edited.
  const proposalKey = preview ? `${preview.layout.sheet}|${preview.layout.headerRow}|${preview.proposedBy}` : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the proposal itself changes
  useEffect(() => {
    if (preview) setMapping(preview.proposal);
  }, [proposalKey]);

  // The AI checks the rules' proposal once, where it's connected (routed by the file's sensitivity).
  useEffect(() => {
    if (!detail || asked.current || !canWrite || !detail.aiAvailable || detail.preview.proposedBy !== 'rules') return;
    asked.current = true;
    suggest.mutate({ sheet: detail.preview.layout.sheet, headerRow: detail.preview.layout.headerRow });
  }, [detail, canWrite, suggest]);

  if (dataset.isError)
    return (
      <EmptyState
        icon={FileSpreadsheet}
        title={dataset.error instanceof ApiError && dataset.error.status === 404 ? 'No dataset here' : 'This file could not be read'}
        learnMore="import-a-dataset"
      >
        {dataset.error instanceof ApiError ? dataset.error.message : 'Try again in a moment.'}
      </EmptyState>
    );
  if (!detail || !preview || !mapping || !snap) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const last = detail.lastImport;
  const showRun = last && (startedId === last.id || !editing);
  const locked = !canWrite || start.isPending;
  return (
    <div className="space-y-6">
      <Link to="/datasets" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> All datasets
      </Link>
      <PageHeader
        title={`Import ${detail.document.filename}`}
        description={`${preview.format.toUpperCase()} · ${preview.layout.dataRows.length} data rows · ${preview.sheets.length} sheet${preview.sheets.length === 1 ? '' : 's'}`}
      />
      <Tip id="dataset-import" learnMore="import-a-dataset">
        Imported values become <em>unreviewed</em> claims linked to their cell (e.g. <DataText>Pilot List!E5</DataText>). A row with the
        same ID code or name as an existing actor updates it instead of creating a duplicate.
      </Tip>
      {!detail.ready && (
        <p role="status" className="rounded-md border border-warning bg-surface px-3 py-2 text-sm">
          This file is still being processed. You can prepare the mapping; importing starts once its card says it is ready.
        </p>
      )}

      {showRun && last ? (
        <div className="space-y-3">
          <ImportRun documentId={id} run={last} />
          {canWrite && (last.stale || (last.status !== 'queued' && last.status !== 'running')) && (
            <Button
              variant="outline"
              onClick={() => {
                setEditing(true);
                setStartedId(null);
                setStep(0);
              }}
            >
              Import again
            </Button>
          )}
        </div>
      ) : (
        <>
          <nav aria-label="Import steps" data-tour="dataset-steps">
            <ol className="flex flex-wrap gap-2">
              {STEPS.map((label, i) => (
                <li key={label}>
                  <button
                    type="button"
                    aria-current={i === step ? 'step' : undefined}
                    onClick={() => setStep(i)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-sm',
                      i === step ? 'border-foreground bg-muted font-medium' : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {i + 1}. {label}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          {detail.aiAvailable && (
            <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="size-4" aria-hidden="true" />
              {suggest.isPending
                ? 'The AI is checking the proposed mapping…'
                : preview.proposedBy === 'ai'
                  ? 'Mapping proposed by the rules and checked by the AI. Confirm or change it.'
                  : suggest.isError
                    ? 'The AI could not check the mapping; the rules proposal stays.'
                    : 'Mapping proposed by the rules.'}
            </p>
          )}

          {step === 0 && (
            <LayoutStep
              preview={preview}
              mapping={mapping}
              snap={snap}
              disabled={locked}
              onLayout={l => {
                suggest.reset();
                setLayout(l);
              }}
              onMapping={setMapping}
            />
          )}
          {step === 1 && <ColumnsStep preview={preview} mapping={mapping} snap={snap} onMapping={setMapping} disabled={locked} />}
          {step === 2 && <CheckStep documentId={id} mapping={mapping} snap={snap} enabled={canWrite} />}

          <div className="flex flex-wrap items-center gap-2 border-t pt-4">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
            {step < 2 && <Button onClick={() => setStep(step + 1)}>Next: {STEPS[step + 1]}</Button>}
            {step === 2 && canWrite && (
              <Button
                disabled={!detail.ready || start.isPending}
                onClick={() =>
                  start.mutate(mapping, {
                    onSuccess: r => {
                      setStartedId(r.import.id);
                      setEditing(false);
                    },
                  })
                }
              >
                Import {preview.layout.dataRows.length} rows
              </Button>
            )}
            {start.error && (
              <p role="alert" className="text-sm text-danger-foreground">
                {start.error instanceof ApiError ? start.error.message : 'The import could not start.'}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
